import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {Modal, ColoredIdIcon, Tooltip, Icon, Alert} from '@forecast-it/design-system';
import Styled from 'styled-components';
import {showModal, MODAL_TYPE} from '../generic_modal_conductor';
import {WinProbabilityStageBands} from './WinProbabilityStageBands';
import {changeWinChance} from '../../../../project-tab/projects/project-settings/ProjectSettingsMutations';
import {createToast} from '../../toasts/toast';
import {graphql} from 'react-relay';
import {ForecastQueryRenderer} from '../../../../../ForecastQueryRenderer';
import {bandForWinChance, toPercent, toStageBands, type StageBand} from '../../../../opportunities-tab/opportunityStage';

export type UpdateWinChanceModalPayload = {
	projectId: string;
	baselineWinChance: number;
	// True when this is a converted (won) opportunity — status ≠ OPPORTUNITY — whose win chance the backend
	// reports as 100% regardless of what is stored. Saving a changed value then also re-opens it to the
	// OPPORTUNITY stage so the new win chance persists.
	reopenToOpportunity?: boolean;
	projectName?: string | null;
	projectCustomId?: string | null; // the "P<companyProjectId>" badge text
	projectColor?: string | null; // badge background
	clientName?: string | null;
	onSuccess?: () => void;
};

type Props = UpdateWinChanceModalPayload & {closeModal: () => void; bands: StageBand[]};

// The three stacked read-only info sections + the picker, spaced per the design.
const Sections = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 32px;
`;
const Field = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
const FieldLabel = Styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	font-weight: 500;
	font-size: 16px;
	line-height: 24px;
	color: #232334;
`;
const ProjectValue = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 16px;
	line-height: 24px;
	color: #232334;
`;
const ClientValue = Styled.div`
	font-size: 16px;
	line-height: 24px;
	color: #232334;
`;
const HelpIcon = Styled.span`
	display: inline-flex;
	cursor: help;
	color: #71717a;
`;

export const UpdateWinChanceModalInner = ({
	projectId,
	baselineWinChance,
	reopenToOpportunity,
	projectName,
	projectCustomId,
	projectColor,
	clientName,
	onSuccess,
	closeModal,
	bands,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const [winChance, setWinChance] = useState(baselineWinChance);

	// "<win probability %> (<stage>)" for a win chance, e.g. "40% (Qualified)" — the opportunity's own
	// percentage, matching the list column and the fixed Lost/Won boundaries below rather than the range.
	const bandStage = (wp: number): string => {
		const resolved = bandForWinChance(bands, wp);
		if (resolved.kind === 'band') {
			return `${toPercent(wp)}% (${resolved.band.name})`;
		}
		if (resolved.kind === 'lost') {
			return `0% (${formatMessage({id: 'opportunities.stage.lost'})})`;
		}
		if (resolved.kind === 'won') {
			return `100% (${formatMessage({id: 'opportunities.stage.won'})})`;
		}
		return '';
	};

	const save = () => {
		// Save is only reachable when the win chance changed (button disabled otherwise), so re-opening a
		// converted opportunity here is always for a genuinely new value.
		changeWinChance(projectId, winChance, !!reopenToOpportunity); // commits via Util.CommitMutation, expects a 0..1 fraction
		const message = formatMessage(
			{id: 'opportunities.update_win_probability.toast'},
			{project: projectName ?? '', from: bandStage(baselineWinChance), to: bandStage(winChance)}
		);
		// A design-system SUCCESS Alert (green surface + tick icon) shown through the toaster's bare top-banner
		// variant, matching the design's full-width success banner rather than the default dark corner toast.
		createToast({duration: 5000, variant: 'banner', message: <Alert type={Alert.TYPE.SUCCESS} text={message} />});
		if (onSuccess) onSuccess();
		closeModal();
	};

	return (
		<Modal onCloseModal={closeModal} size={Modal.SIZE.EXTRA_LARGE}>
			<Modal.Title>{formatMessage({id: 'opportunities.update_win_probability.title'})}</Modal.Title>
			<Modal.Content>
				<Sections>
					<Field data-cy="opportunity-project">
						<FieldLabel>{formatMessage({id: 'common.project'})}</FieldLabel>
						<ProjectValue>
							{projectCustomId ? (
								<ColoredIdIcon color={projectColor ?? undefined}>{projectCustomId}</ColoredIdIcon>
							) : null}
							<span>{projectName}</span>
						</ProjectValue>
					</Field>
					<Field data-cy="opportunity-client">
						<FieldLabel>{formatMessage({id: 'common.client'})}</FieldLabel>
						<ClientValue>{clientName}</ClientValue>
					</Field>
					<Field>
						<FieldLabel>
							{formatMessage({id: 'opportunities.update_win_probability.section'})}
							<Tooltip content={formatMessage({id: 'opportunities.update_win_probability.help'})}>
								<HelpIcon data-cy="win-probability-help">
									<Icon icon="help" size="s" />
								</HelpIcon>
							</Tooltip>
						</FieldLabel>
						<WinProbabilityStageBands bands={bands} value={winChance} onChange={setWinChance} />
					</Field>
				</Sections>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton dataCy="save-win-chance" onPress={save} disabled={winChance === baselineWinChance}>
					{formatMessage({id: 'common.update'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton dataCy="cancel-win-chance" onPress={closeModal}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

const updateWinChanceModalQuery = graphql`
	query UpdateWinChanceModal_Query {
		viewer {
			actualPersonId
			component(name: "update_win_chance_modal")
			company {
				id
				opportunityStageBands(first: 1000) {
					edges {
						node {
							id
							name
							startPercentage
							endPercentage
							winProbability
						}
					}
				}
			}
		}
	}
`;

/**
 * The picker needs the account's stage mapping, so the modal loads one. Everything else it shows comes
 * from the payload and is on screen before the query resolves; only the picker waits, behind the query
 * renderer's default loader.
 */
export const UpdateWinChanceModal = (props: UpdateWinChanceModalPayload & {closeModal: () => void}): JSX.Element => (
	<ForecastQueryRenderer
		key="query-render-updateWinChanceModal"
		query={updateWinChanceModalQuery}
		modal={true}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed by the generated query
		render={(relayProps: any) => (
			<UpdateWinChanceModalInner {...props} bands={toStageBands(relayProps.viewer.company.opportunityStageBands)} />
		)}
	/>
);

export const showUpdateWinChanceModal = (payload: UpdateWinChanceModalPayload) =>
	showModal({type: MODAL_TYPE.UPDATE_WIN_CHANCE, ...payload});

export default UpdateWinChanceModal;
